import { FC, useState } from 'react';
import RichTextEditor, {
  EditorValue,
  ToolbarConfig,
} from 'src/lib/rich-text-editor';
import '../../../css/WYSIWYG.css';

interface Props {
  value: string;
  onChange?: (value: { html: string; markdown: string }) => void;
  placeholder?: string;
  reducedMargin?: boolean;
  framed?: boolean;
  formatType?: 'html' | 'markdown';
  disabled?: boolean;
  readOnly?: boolean;
  maxMarkdownLength?: number;
}

export const WYSIWYG: FC<Props> = ({
  value,
  onChange,
  placeholder,
  reducedMargin,
  framed,
  formatType = 'markdown',
  readOnly,
  maxMarkdownLength,
  ...props
}) => {
  const [editorValue, setEditorValue] = useState(
    EditorValue.createFromString(value, formatType),
  );

  const handleChange = (val: EditorValue) => {
    const markdown = val.toString('markdown');
    if (maxMarkdownLength != null && markdown.length > maxMarkdownLength) {
      const truncated = markdown.slice(0, maxMarkdownLength);
      const truncatedVal = EditorValue.createFromString(truncated, 'markdown');
      setEditorValue(truncatedVal);
      onChange?.({
        html: truncatedVal.toString('html'),
        markdown: truncated,
      });
      return;
    }
    setEditorValue(val);
    onChange?.({
      html: val.toString('html'),
      markdown,
    });
  };

  const customEditorStyle = reducedMargin
    ? {
        padding: '8px 16px',
        minHeight: '120px',
        boxSizing: 'border-box' as const,
      }
    : undefined;

  const rootClass = ['editor-box', framed && 'editor-box--framed']
    .filter(Boolean)
    .join(' ');

  return (
    <RichTextEditor
      className={rootClass}
      toolbarClassName="custom-toolbar"
      editorClassName="custom-editor"
      placeholder={placeholder}
      toolbarStyle={{ margin: 0, position: 'relative' }}
      editorStyle={customEditorStyle}
      readOnly={readOnly}
      toolbarConfig={
        {
          extraProps: { className: 'extra-prop' },
          display: [
            'BLOCK_TYPE_DROPDOWN',
            'INLINE_STYLE_BUTTONS',
            'BLOCK_TYPE_BUTTONS',
            'HISTORY_BUTTONS',
          ],
          BLOCK_TYPE_DROPDOWN: [
            { label: 'Normal', style: 'unstyled' },
            { label: 'Heading Large', style: 'header-one' },
            { label: 'Heading Medium', style: 'header-two' },
            { label: 'Heading Small', style: 'header-three' },
          ],
          INLINE_STYLE_BUTTONS: [
            {
              label: 'B',
              style: 'BOLD',
              className: 'custom-button-style',
            },
            {
              label: 'I',
              style: 'ITALIC',
              className: 'custom-button-style',
            },
            {
              label: 'U',
              style: 'UNDERLINE',
              className: 'custom-button-style',
            },
          ],
          BLOCK_TYPE_BUTTONS: [
            {
              label: 'UL',
              style: 'unordered-list-item',
              className: 'custom-button-style',
            },
            {
              label: 'OL',
              style: 'ordered-list-item',
              className: 'custom-button-style',
            },
          ],
          BLOCK_ALIGNMENT_BUTTONS: [],
        } satisfies ToolbarConfig
      }
      value={editorValue}
      onChange={handleChange}
      disabled={props.disabled}
    />
  );
};
